/**
 * Corpus browser API client (M2 v2 Stage A, P-A5).
 *
 * Talks to 4 Worker endpoints under /api/v1/* per src/workers/api/corpus-browser-m2/handlers.ts.
 * Shape mirrors v2 types (src/workers/api/corpus-browser-m2/types.ts).
 * Network/HTTP errors throw Error - consumers render banner UI.
 *
 * Endpoints (live https://asklaw.vn):
 *   GET /api/v1/search?q=&type=&year=&status=&org=&is_lw=&scope=&page=&limit=
 *   GET /api/v1/doc/:id
 *   GET /api/v1/doc/:id/raw-html   (text/html stream, not JSON)
 *   GET /api/v1/doc/:id/references (returns references_in + references_out)
 *
 * Spec drift note (2026-05-21): replaced POC v1 schema (provisions + pdf-redirect)
 * with v2 raw-html iframe pattern. Old fetchProvisions/fetchPdfRedirect dropped.
 */

export type EffStatusCode = 'CHL' | 'HHL' | 'HHL1P' | 'TNHL' | 'CHHL' | 'UNKNOWN';
export type ScopeCode = 'TW' | 'DP';
export type SearchSource = 'meta' | 'body' | 'cascade';

/** Search hit row (v2 schema). */
export interface SearchHit {
  doc_id: string;
  title: string;
  doc_num: string | null;
  doc_type_code: string | null;
  agency_name: string | null;
  issue_date: string | null;
  eff_status_code: string | null;
  scope: ScopeCode;
  snippet: string;
  score: number;
}

/** Search filter URL params. */
export interface SearchFilters {
  q?: string;
  doc_type?: string;
  year?: string;
  eff_status?: string;
  org?: string;
  is_lw?: 0 | 1;
  scope?: ScopeCode | 'ALL';
  page?: number;
  limit?: number;
}

/** Search response envelope (v2 schema). */
export interface SearchResponse {
  query: string;
  query_rewritten?: string | null;
  filters: Omit<SearchFilters, 'q' | 'page' | 'limit'>;
  total: number;
  page: number;
  per_page: number;
  source: SearchSource;
  latency_ms: number;
  cache_hit: boolean;
  results: SearchHit[];
}

/** Doc detail response (v2 schema). */
export interface DocDetail {
  doc_id: string;
  scope: ScopeCode;
  is_lw: number;
  doc_type_code: string | null;
  doc_type_name: string | null;
  doc_num: string | null;
  title: string;
  agency_name: string | null;
  org_code: string | null;
  org_name: string | null;
  issue_date: string | null;
  eff_from: string | null;
  eff_to: string | null;
  eff_status_code: EffStatusCode | null;
  eff_status_name: string | null;
  lang: string | null;
  is_translation: number | null;
  is_consolidated: number | null;
  summary_text: string | null;
  summary_source: string | null;
  ref_count: number;
  raw_html_url: string;
  raw_json_url: string | null;
  /** Sprint 5 SB5-01: multi-source ingest tier (per migration 0048). */
  source_tier?: 'vbpl_primary' | 'vanban_pdf' | 'cong_bao_html' | null;
  /** Sprint 5 SB5-01: PDF.js viewer source URL (null when no PDF asset). */
  raw_pdf_url?: string | null;
  /** Sprint 5 SB5-01: external docid for outbound link to upstream source. */
  source_external_id?: string | null;
  ingested_at: string | null;
  updated_at: string | null;
}

/** Reference edge (v2 schema). */
export interface ReferenceEdge {
  source_doc_id: string;
  target_doc_id: string;
  ref_type: number;
  ref_label: string;
  title: string | null;
  doc_num: string | null;
  issue_date: string | null;
  in_pool: boolean;
}

export interface ReferencesResponse {
  doc_id: string;
  references_in: ReferenceEdge[];
  references_out: ReferenceEdge[];
  total_in: number;
  total_out: number;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) {
    throw new Error('not_found');
  }
  if (!res.ok) {
    throw new Error(`http_${res.status}`);
  }
  return (await res.json()) as T;
}

export async function searchCorpus(filters: SearchFilters): Promise<SearchResponse> {
  const qs = new URLSearchParams();
  if (filters.q) qs.set('q', filters.q);
  if (filters.doc_type) qs.set('type', filters.doc_type);
  if (filters.year) qs.set('year', filters.year);
  if (filters.eff_status) qs.set('status', filters.eff_status);
  if (filters.org) qs.set('org', filters.org);
  if (filters.is_lw === 0 || filters.is_lw === 1) qs.set('is_lw', String(filters.is_lw));
  if (filters.scope && filters.scope !== 'ALL') qs.set('scope', filters.scope);
  if (filters.page && filters.page > 1) qs.set('page', String(filters.page));
  if (filters.limit) qs.set('limit', String(filters.limit));
  return getJson<SearchResponse>(`/api/v1/search?${qs.toString()}`);
}

export async function fetchDocDetail(docId: string): Promise<DocDetail> {
  return getJson<DocDetail>(`/api/v1/doc/${encodeURIComponent(docId)}`);
}

export async function fetchReferences(docId: string): Promise<ReferencesResponse> {
  return getJson<ReferencesResponse>(`/api/v1/doc/${encodeURIComponent(docId)}/references`);
}

/**
 * Fetch raw HTML body as text. Used by RawHtmlViewer iframe srcdoc render.
 * 404 throws 'not_found' so consumer can render fallback.
 */
export async function fetchRawHtml(docId: string): Promise<string> {
  const url = `/api/v1/doc/${encodeURIComponent(docId)}/raw-html`;
  const res = await fetch(url, { headers: { Accept: 'text/html' } });
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) throw new Error(`http_${res.status}`);
  return await res.text();
}

// ---------------------------------------------------------------------------
// Labels - VN copy with full diacritics
// ---------------------------------------------------------------------------

export const EFF_STATUS_LABEL_VI: Record<string, string> = {
  CHL: 'Còn hiệu lực',
  HHL: 'Hết hiệu lực',
  HHL1P: 'Hết hiệu lực một phần',
  TNHL: 'Tạm ngưng hiệu lực',
  TNHL1P: 'Tạm ngưng hiệu lực một phần',
  CHHL: 'Chưa có hiệu lực',
  CCHL: 'Chưa có hiệu lực',
  KCPH: 'Không công bố',
  KXĐ: 'Không xác định',
  KXD: 'Không xác định',
  UNKNOWN: 'Chưa rõ',
};

export const SCOPE_LABEL_VI: Record<ScopeCode, string> = {
  TW: 'Trung ương',
  DP: 'Địa phương',
};

export const DOC_TYPE_LABEL_VI: Record<string, string> = {
  bo_luat: 'Bộ luật',
  luat: 'Luật',
  nghi_dinh: 'Nghị định',
  thong_tu: 'Thông tư',
  quyet_dinh: 'Quyết định',
  nghi_quyet: 'Nghị quyết',
  chi_thi: 'Chỉ thị',
  thong_tu_lien_tich: 'Thông tư liên tịch',
  thong_bao: 'Thông báo',
  cong_van: 'Công văn',
};

/**
 * 11 verified refType labels per memory reference_vbpl_reference_types
 * (updated 2026-05-05, ~99.5% edges). Codes 8/13/14 fall back to "Khác".
 * MUST mirror Worker src/workers/api/corpus-browser-m2/types.ts REF_TYPE_LABEL.
 */
export const REF_TYPE_VERIFIED: Record<number, { label: string; color: string }> = {
  3: { label: 'Căn cứ ban hành', color: '#2b7cb3' },
  4: { label: 'Văn bản được dẫn chiếu', color: '#0d9488' },
  10: { label: 'Văn bản được sửa đổi bổ sung', color: '#ea580c' },
  12: { label: 'Văn bản được thay thế (một phần)', color: '#7c3aed' },
  1: { label: 'Văn bản được thay thế', color: '#6d28d9' },
  9: { label: 'Văn bản được hướng dẫn', color: '#16a34a' },
  2: { label: 'Văn bản tiếng Anh', color: '#64748b' },
  7: { label: 'Văn bản được hợp nhất', color: '#0f766e' },
  6: { label: 'Văn bản đính chính', color: '#d97706' },
  11: { label: 'Văn bản hết hiệu lực', color: '#dc2626' },
  5: { label: 'Văn bản hết hiệu lực một phần', color: '#e11d48' },
};

export function refTypeLabel(code: number): { label: string; color: string } {
  const verified = REF_TYPE_VERIFIED[code];
  if (verified) return verified;
  return { label: 'Khác', color: '#6b7280' };
}

export function effStatusLabel(code: string | null | undefined): string {
  if (!code) return EFF_STATUS_LABEL_VI.UNKNOWN;
  return EFF_STATUS_LABEL_VI[code] ?? EFF_STATUS_LABEL_VI.UNKNOWN;
}
